import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { useIntl } from "react-intl";
import { Grid, Loader, Message, Tab } from "semantic-ui-react";
import _ from "lodash";

import {
  EditableCampaign,
  SelectedPrompts,
  CampaignEditRequest,
  PromptCampaign,
  Category,
  CampaignStep,
  WebComponents,
  CampaignAccess,
} from "@/interfaces";

import { ConfirmationModal, AutoSubscriptions, EnrolledStatistics, PageActionProps } from "@/components";

import { CampaignServices } from "@/services";
import { useAccountSettings, URLS, useSocialCoachSession } from "@/utils";

import { CampaignReducer } from "./reducers";
import { actions } from "./reducers/actions";
import { CampaignInitialState } from "./reducers/initialState";

import { InformationComponent, PromptsStep } from "./components";
import { CampaignPageType, descriptors } from "./descriptors";

import "./styles.scss";
import { EligibilityEditor } from "@/containers";
import { useMemo } from "react";
import { PageWrapper } from "@/components/PageWrapper";
import CampaignStats from "./components/CampaignStats";

type Props = RouteComponentProps;

const campaignCookie = "coachEditCampaign";
const campaignTab = "coachEditCampaignTab";

const CampaignFC: React.FC<Props> = ({ history, match: { params } }) => {
  const [state, dispatch] = React.useReducer(CampaignReducer, CampaignInitialState);
  const { formatMessage } = useIntl();

  const { coachId } = useSocialCoachSession();
  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = hasWritePermission(WebComponents.CAMPAIGNS);

  const [campaign, setCampaign] = React.useState<EditableCampaign | undefined>(undefined);
  const [campaignAccess, setCampaignAccess] = React.useState<CampaignAccess>();
  const infoFormRef = React.useRef<HTMLFormElement>(null);

  const [submitError, setSubmitError] = React.useState<string | undefined>(undefined);
  const [stepError, setStepError] = React.useState<string | undefined>(undefined);
  const [status, setStatus] = React.useState<string | undefined>(undefined);
  const [initialPrompts, setInitialPrompts] = React.useState<string[]>([]);

  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [promptsUpdated, setPromptsUpdated] = React.useState<boolean>(false);

  const [showPromptSection, setShowPromptSection] = React.useState<boolean>(false);

  const campaignId = params["campaignId"];
  const isActive = campaign?.status === "ACTIVE";

  const tabPosition = {
    INFO: 0,
    SEGMENTS: 1,
    SUBSCRIBERS: 2,
  };

  const selectedTab = localStorage.getItem(campaignTab) ? localStorage.getItem(campaignTab)! : "INFO";
  const [selectedIndex, setSelectedIndex] = React.useState<number>(tabPosition[selectedTab] || 0);

  let nextSelectedIndex = selectedIndex;

  const stepsValidations: { [key: string]: CampaignStep } = {
    0: {
      validations: () =>
        new Promise((_, reject) => {
          if (infoFormRef.current) {
            infoFormRef!.current!.handleSubmit();
          }
          reject();
        }),
    },
    1: {
      validations: () =>
        new Promise(resolve => {
          resolve("");
        }),
    },
    2: {
      validations: () =>
        new Promise(resolve => {
          resolve("");
        }),
    },
    3: {
      validations: () =>
        new Promise(resolve => {
          resolve("");
        }),
    },
    4: {
      validations: () =>
        new Promise(resolve => {
          resolve("");
        }),
    },
  };

  React.useEffect(() => {
    if (campaignId) {
      setIsSubmitting(true);
      CampaignServices.get(campaignId)
        .then(response => {
          const cPrompts = response.campaignPrompts || [];
          setCampaign(response);
          setCampaignAccess(response.campaignAccess);
          setInitialPrompts(cPrompts.map((p: PromptCampaign) => String(p.prompt!.promptId)));
        })
        .catch(() => {
          setSubmitError("Error loading this campaign");
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  }, [params["campaignId"]]);

  React.useEffect(() => {
    if (status) {
      save();
    }
  }, [status]);

  React.useEffect(() => {
    if (selectedIndex !== undefined) {
      localStorage.setItem(campaignTab, `${selectedIndex}`);
    }
  }, [selectedIndex]);

  React.useEffect(() => {
    setSubmitError(undefined);
  }, [showPromptSection]);

  React.useEffect(() => {
    localStorage.setItem(campaignCookie, JSON.stringify(campaign));
  }, [campaign]);

  const saveCampaign = async (
    campaignStatus: string = "ACTIVE",
    campaignToSave: EditableCampaign,
    shuffle: boolean = promptsUpdated
  ): Promise<any> => {
    try {
      if (campaignToSave) {
        const {
          name,
          isAutoPost,
          autoEnroll,
          campaignPrompts,
          deliveryMode,
          randomScheduleDays,
          categoryIds,
          randomScheduleDeliveryTimePst,
          startDate,
          endDate,
        } = campaignToSave;

        const newCampaignPrompts = campaignPrompts!.map((cPrompt: PromptCampaign) => {
          const { prompt, socialNetworks, campaignPromptId, deliveryDatetimeUtc } = cPrompt;
          return {
            promptId: prompt!.promptId,
            campaignId: campaignPromptId,
            socialNetworks,
            sortOrder: cPrompt.sortOrder,
            deliveryDatetimeUtc: deliveryMode === "SPECIFIC_DATE" ? deliveryDatetimeUtc : undefined,
          } as PromptCampaign;
        });

        let campaignRequest: CampaignEditRequest = {
          campaignId,
          name,
          isAutoPost,
          autoEnroll,
          campaignPrompts: newCampaignPrompts,
          categoryIds,
          deliveryMode,
          status: campaignStatus,
          campaignAccess,
        };

        if (deliveryMode !== "SPECIFIC_DATE") {
          campaignRequest = {
            ...campaignRequest,
            randomScheduleDays,
            randomScheduleDeliveryTimePst,
            shuffle,
            startDate,
            endDate,
          };
        }
        setIsSubmitting(true);
        CampaignServices.update(campaignRequest, coachId!)
          .then(() => {
            closeAndClean();
            setSubmitError(undefined);
          })
          .catch(e => {
            setSubmitError(e.length < 150 ? e : formatMessage({ ...descriptors[CampaignPageType.savingError] }));
          })
          .finally(() => {
            setIsSubmitting(false);
          });
      }
    } catch (e: any) {
      setSubmitError(e);
    }
  };

  const save = (): void => {
    stepsValidations[selectedIndex]
      .validations()
      .then(() => {
        saveCampaign(status, campaign!);
      })
      .catch(e => {
        if (e && e !== "") {
          setStepError(e);
        }
      });
  };

  const saveCampaignAction = (campaignStatus: "ACTIVE" | "UNPUBLISHED" | "DELETED"): void => {
    if (status === campaignStatus) {
      save();
    } else {
      setStatus(campaignStatus);
    }
  };

  const onSaveDraftAction = () => {
    saveCampaignAction("UNPUBLISHED");
  };

  const onDeleteAction = () => {
    if (window.confirm("Are you sure you would like to remove this campaign?")) {
      saveCampaignAction("DELETED");
    }
  };

  const onModalSave = () => {
    tabValidationsPromise()
      .then(() => {
        saveCampaign(status, campaign!);
      })
      .catch(e => {
        if (e && e !== "") {
          setStepError(e);
        }
      });
    dispatch({ name: actions.CLOSE_CONFIRMATION_MODAL });
  };

  const closeAndClean = () => {
    localStorage.removeItem(campaignCookie);
    localStorage.removeItem(campaignTab);

    dispatch({ name: actions.CANCEL });
    history.push(URLS.coach.campaigns);
  };

  const campaignsPromptsConverted = (): SelectedPrompts => {
    if (campaign?.campaignPrompts) {
      let selectedPrompts: SelectedPrompts = {};
      campaign?.campaignPrompts.map((cPrompt: PromptCampaign) => {
        const { promptId, title, post, categories } = cPrompt.prompt!;
        selectedPrompts = {
          ...selectedPrompts,
          [promptId!]: {
            title,
            post,
            prompt: cPrompt.prompt,
            categoryIds: categories ? categories!.map((category: Category) => category!.categoryId!) : [],
          },
        };
      });
      return selectedPrompts;
    }

    return {};
  };

  const tabValidationsPromise = (): Promise<any> => {
    return stepsValidations[selectedIndex].validations();
  };

  const tabOnChangeHandler = (nextIndex: number) => {
    nextSelectedIndex = nextIndex;
    if (selectedIndex <= 4 && selectedIndex >= 0 && nextSelectedIndex !== selectedIndex) {
      tabValidationsPromise()
        .then(() => {
          setSelectedIndex(nextIndex || 0);
          setStepError(undefined);
        })
        .catch(e => {
          if (e && e !== "") {
            setStepError(e);
          }
        });
    }
  };

  const promptsSuccessHandler = (selectedPrompts: SelectedPrompts) => {
    if (Object.keys(selectedPrompts).length > 0) {
      setStepError(undefined);
    }
    const firstPrompt = campaign?.campaignPrompts[0];

    const campaignPrompts = Object.keys(selectedPrompts).map(promptId => {
      const isPrevious = campaign?.campaignPrompts.filter(c => `${c.prompt!.promptId}` === `${promptId}`);
      return {
        promptId: promptId!,
        socialNetworks:
          isPrevious && isPrevious!.length > 0 ? isPrevious[0].socialNetworks : firstPrompt?.socialNetworks,
        prompt: {
          ...selectedPrompts[promptId].prompt,
          deliveryDatetimeUtc: isPrevious && isPrevious!.length > 0 ? isPrevious[0].deliveryDatetimeUtc : undefined,
        },
        deliveryDatetimeUtc: isPrevious && isPrevious!.length > 0 ? isPrevious[0].deliveryDatetimeUtc : undefined,
      } as PromptCampaign;
    });

    setShowPromptSection(false);
    setCampaign({ ...campaign!, campaignPrompts });
    setPromptsUpdated(true);
  };

  const informationOnSuccessHandler = (updatedCampaign: EditableCampaign) => {
    const shufflePrompts = _.isEqual(updatedCampaign.campaignPrompts, campaign?.campaignPrompts) === false;
    if (shufflePrompts) {
      setPromptsUpdated(true);
    }

    setCampaign(updatedCampaign);

    if (nextSelectedIndex === selectedIndex) {
      saveCampaign(status, updatedCampaign, shufflePrompts);
    } else {
      setSelectedIndex(nextSelectedIndex);
    }
  };

  const subscriptionOnChangeHandler = (playersId: number[]) => {
    setCampaignAccess(c => {
      return {
        ...c,
        subscribedPlayerIds: playersId,
        pendingPlayerIds: (c?.pendingPlayerIds || []).filter(id => playersId.includes(id)),
      } as CampaignAccess;
    });
  };

  const confirmationModal = () => {
    return (
      <ConfirmationModal
        openConfirmationModal={state.openConfirmationModal}
        onClose={() => {
          dispatch({ name: actions.CLOSE_CONFIRMATION_MODAL });
        }}
        okHandler={onModalSave}
        rejectHandler={closeAndClean}
      />
    );
  };

  const selectedPlayerIds: number[] = useMemo(() => {
    if (campaignAccess) {
      const merged = campaignAccess.subscribedPlayerIds.concat(campaignAccess.pendingPlayerIds || []);
      return Array.from(new Set(merged));
    } else {
      return [];
    }
  }, [campaignAccess]);

  const panes = [
    {
      menuItem: "Edit Campaign",
      render: () => (
        <Tab.Pane loading={isSubmitting}>
          {campaign && (
            <InformationComponent
              disabled={!withWritePermission}
              submitRef={infoFormRef}
              campaign={campaign!}
              initialPromptsIds={initialPrompts}
              editable={
                !(
                  campaign.deliveryMode !== "SPECIFIC_DATE" &&
                  _.get(campaign.campaignAccess, "subscribedPlayerIds", []).length > 0 &&
                  isActive
                )
              }
              onSuccessHandler={informationOnSuccessHandler}
              addPromptHandler={() => {
                setShowPromptSection(true);
              }}
            />
          )}
        </Tab.Pane>
      ),
    },
    {
      menuItem: formatMessage({
        ...descriptors[CampaignPageType.segmentationSectionTitle],
      }),
      render: () => (
        <Tab.Pane loading={isSubmitting}>
          <Grid.Column width={10} className={"eligibilityContainer"}>
            {campaign?.campaignAccess && campaignAccess && (
              <EligibilityEditor
                isNew={!campaign?.campaignId}
                originalAccess={campaign?.campaignAccess}
                currentAccess={campaignAccess}
                accessType={"CAMPAIGNS"}
                updateHandler={async newCriteria => {
                  setCampaignAccess(c => {
                    return { ...c, eligibilityCriteria: newCriteria } as CampaignAccess;
                  });
                  let newAccess = await CampaignServices.getAccessPreview(
                    campaign?.campaignId?.toString()!,
                    newCriteria
                  );
                  setCampaignAccess(newAccess);
                  return newAccess;
                }}
              />
            )}
          </Grid.Column>
        </Tab.Pane>
      ),
    },
    {
      menuItem: formatMessage({
        ...descriptors[CampaignPageType.autoSubcribeSectionTitle],
      }),
      render: () => (
        <Tab.Pane loading={isSubmitting}>
          <AutoSubscriptions
            disabled={!withWritePermission}
            eligiblePlayerIds={campaignAccess?.eligiblePlayerIds || []}
            isInclude={true}
            selectedPlayers={selectedPlayerIds}
            onChange={subscriptionOnChangeHandler}
            autoEnroll={campaign?.autoEnroll}
            onAutoEnrollChange={autoEnroll => {
              setCampaign(c => ({ ...c!, autoEnroll }));
            }}
            isAutoPost={campaign?.isAutoPost}
          />
        </Tab.Pane>
      ),
    },
    {
      menuItem: formatMessage({
        ...descriptors[CampaignPageType.enrolledSectionTitle],
      }),
      render: () => (
        <Tab.Pane loading={isSubmitting}>
          <EnrolledStatistics
            campaignId={campaign?.campaignId}
            subscribedIds={campaign?.campaignAccess?.subscribedPlayerIds || []}
            pendingIds={campaign?.campaignAccess?.pendingPlayerIds || []}
          />
        </Tab.Pane>
      ),
    },
    {
      menuItem: "Performance",
      render: () => (
        <Tab.Pane className={"statsPane"}>
          <Grid.Column width={10} className={"formContainer"}>
            <CampaignStats campaign={campaign} />
          </Grid.Column>
        </Tab.Pane>
      ),
    },
  ];

  const content = () => {
    return showPromptSection ? (
      <PromptsStep
        coachId={coachId?.toString()!}
        campaignPrompts={campaignsPromptsConverted()}
        onChangeHandler={promptsSuccessHandler}
        onCancel={() => setShowPromptSection(false)}
      />
    ) : (
      <Tab
        panes={panes}
        activeIndex={selectedIndex}
        onTabChange={(_, data) => {
          tabOnChangeHandler(Number(data.activeIndex!));
        }}
      />
    );
  };

  const pageActions = useMemo(() => {
    const actions: PageActionProps[] = [];
    if (withWritePermission) {
      actions.push({
        text: isActive ? "Save" : "Publish Campaign",
        onClick: () => {
          saveCampaignAction("ACTIVE");
        },
        loading: isSubmitting,
      });
      actions.push({
        text: isActive ? "Unpublish Campaign" : "Save As Draft",
        onClick: () => {
          onSaveDraftAction();
        },
        loading: isSubmitting,
      });
      actions.push({
        text: "Delete Campaign",
        onClick: () => {
          onDeleteAction();
        },
        loading: isSubmitting,
      });
    }

    return actions;
  }, [withWritePermission, isSubmitting, isActive]);

  return (
    <PageWrapper title={"Edit Campaign"} actions={pageActions} loading={isSubmitting}>
      {(stepError || submitError) && (
        <Grid.Row>
          <Message className={"leftMargin"} error list={[submitError, stepError]} />
        </Grid.Row>
      )}
      <Grid.Row className={"campaignEditContainer"}>
        <Grid.Column width={16} className={"editColumn"}>
          {isSubmitting ? <Loader active size="large" /> : content()}
        </Grid.Column>
      </Grid.Row>

      {confirmationModal()}
    </PageWrapper>
  );
};

export const EditCampaignPage = withRouter(CampaignFC);
